"use client";

import { motion } from "framer-motion";
import { Icon } from "@/components/ui/Icon";
import Image, { type StaticImageData } from "next/image";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";
import imageAeroport from "@/assets/images/chauffeur/aeroport/Transferts-aeroports-heliports.png";
import imageSuiviVol from "@/assets/images/chauffeur/aeroport/suivi-vol.png";
import imageAccueilPancarte from "@/assets/images/chauffeur/aeroport/accueil-pancarte.png";
import imageTarifsForfaitaires from "@/assets/images/chauffeur/aeroport/tarifs-forfaitaires.png";
import imageChefDeProjet from "@/assets/images/chauffeur/events/avantages/Chef-de-projet.png";
import imageParcAdaptable from "@/assets/images/chauffeur/events/avantages/Parc-adaptable.png";
import imageItinerairesOptimises from "@/assets/images/chauffeur/events/avantages/Itineraires-optimises.png";
import imageSlaFiabilite from "@/assets/images/chauffeur/entreprise/atouts/SLA-fiabilite.png";
import imageFacturationClaire from "@/assets/images/chauffeur/entreprise/atouts/Facturation-claire.png";
import imageSupportDedie from "@/assets/images/chauffeur/entreprise/atouts/Support-dédie.png";
import imagePonctualite from "@/assets/images/chauffeur/services/atouts/Ponctualite.png";
import imageConfortPremium from "@/assets/images/chauffeur/services/atouts/Confort-premium.png";
import imageExperienceLocale from "@/assets/images/chauffeur/services/atouts/Experience-locale.png";
import imageTransfertSuiviVol from "@/assets/images/chauffeur/transfert/atouts/Suivi-de-vol.png";
import imageTransfertAccueilDedie from "@/assets/images/chauffeur/transfert/atouts/Accueil-dedie.png";
import imageTransfertForfaitsClairs from "@/assets/images/chauffeur/transfert/atouts/Forfaits-clairs.png";
import imageAccesEvenements from "@/assets/images/chauffeur/cannes/atouts/Acces-evenements.png";
import imageFlotteAdaptableCannes from "@/assets/images/chauffeur/cannes/atouts/flotte-adaptable.png";
import imageChauffeursBilingues from "@/assets/images/chauffeur/cannes/atouts/Chauffeurs-bilingues.png";
import imageConnaissanceLocaleCoteAzur from "@/assets/images/chauffeur/cote-azur/atouts/Connaissance-locale.png";
import imageReseauPartenairesCoteAzur from "@/assets/images/chauffeur/cote-azur/atouts/Reseau-partenaires.png";
import imageSupport247CoteAzur from "@/assets/images/chauffeur/cote-azur/atouts/Support-24-7.png";
import imageComfort from "@/assets/images/chauffeur/Ambiance-interieur-premium.png";
import imageService from "@/assets/images/chauffeur/services/Experience-passager.png";
import imageAccueil from "@/assets/images/chauffeur/Prise-en-charge-aeroport-hotel.png";

type Benefit = { title: string; description: string; icon?: string };

type BenefitsProps = {
  heading: string;
  items: Benefit[];
  variant?: "dark" | "light";
  locale?: Locale;
};

const resolveBenefitImage = (
  title: string,
  sectionHeading: string,
  index: number,
): StaticImageData => {
  const label = title.toLowerCase();
  const heading = sectionHeading.toLowerCase();
  if (heading.includes("pourquoi choisir nos chauffeurs privés")) {
    if (
      label.includes("ponctualité 24/7") ||
      label.includes("ponctualite 24/7")
    ) {
      return imagePonctualite;
    }
    if (label.includes("confort premium")) return imageConfortPremium;
    if (
      label.includes("expérience locale") ||
      label.includes("experience locale")
    ) {
      return imageExperienceLocale;
    }
  }
  if (heading.includes("vos avantages transfert")) {
    if (label.includes("suivi de vol")) return imageTransfertSuiviVol;
    if (label.includes("accueil dédié") || label.includes("accueil dedie")) {
      return imageTransfertAccueilDedie;
    }
    if (label.includes("forfaits clairs")) return imageTransfertForfaitsClairs;
  }
  if (heading.includes("pourquoi cannes avec nous")) {
    if (
      label.includes("accès événements") ||
      label.includes("acces evenements")
    ) {
      return imageAccesEvenements;
    }
    if (label.includes("flotte adaptable")) return imageFlotteAdaptableCannes;
    if (
      label.includes("chauffeurs bilingues") ||
      label.includes("chauffeur bilingue")
    ) {
      return imageChauffeursBilingues;
    }
  }
  if (heading.includes("vos bénéfices sur la côte")) {
    if (
      label.includes("connaissance locale") ||
      label.includes("connaissance")
    ) {
      return imageConnaissanceLocaleCoteAzur;
    }
    if (
      label.includes("réseau partenaires") ||
      label.includes("reseau partenaires")
    ) {
      return imageReseauPartenairesCoteAzur;
    }
    if (label.includes("support 24/7") || label.includes("support 24-7")) {
      return imageSupport247CoteAzur;
    }
  }
  if (label.includes("suivi de vol")) return imageSuiviVol;
  if (label.includes("accueil avec pancarte")) return imageAccueilPancarte;
  if (label.includes("tarifs forfaitaires")) return imageTarifsForfaitaires;
  if (label.includes("chef de projet")) return imageChefDeProjet;
  if (label.includes("parc adaptable")) return imageParcAdaptable;
  if (label.includes("sla & fiabilité") || label.includes("sla & fiabilite")) {
    return imageSlaFiabilite;
  }
  if (label.includes("facturation claire")) return imageFacturationClaire;
  if (label.includes("support dédié") || label.includes("support dedie")) {
    return imageSupportDedie;
  }
  if (
    label.includes("itinéraires optimisés") ||
    label.includes("itineraires optimises")
  ) {
    return imageItinerairesOptimises;
  }
  // Repli anglais (la version EN ne matche pas les libellés FR ci-dessus).
  // Mappe les mots-clés EN vers les mêmes visuels → cartes EN illustrées.
  if (label.includes("flight tracking")) return imageTransfertSuiviVol;
  if (label.includes("name-board") || label.includes("name board")) {
    return imageTransfertAccueilDedie;
  }
  if (label.includes("flat rate") || label.includes("clear rate")) {
    return imageTransfertForfaitsClairs;
  }
  if (label.includes("project manager")) return imageChefDeProjet;
  if (label.includes("adaptable fleet") || label.includes("flexible fleet")) {
    return imageParcAdaptable;
  }
  if (
    label.includes("optimised routes") ||
    label.includes("optimized routes")
  ) {
    return imageItinerairesOptimises;
  }
  if (label.includes("reliability") || label.includes("sla")) {
    return imageSlaFiabilite;
  }
  if (label.includes("clear billing") || label.includes("billing")) {
    return imageFacturationClaire;
  }
  if (label.includes("dedicated support") || label.includes("support")) {
    return imageSupportDedie;
  }
  if (label.includes("events access") || label.includes("event access")) {
    return imageAccesEvenements;
  }
  if (label.includes("bilingual")) return imageChauffeursBilingues;
  if (label.includes("local knowledge") || label.includes("local experience")) {
    return imageExperienceLocale;
  }
  if (
    label.includes("aéroport") ||
    label.includes("aeroport") ||
    label.includes("airport") ||
    label.includes("vol") ||
    label.includes("flight") ||
    label.includes("pancarte")
  ) {
    // Image aéroport côté bénéfices distincte de celle des services
    // (imageAeroport) → évite le doublon quand les 2 mentionnent l'aéroport.
    return imageSuiviVol;
  }
  if (
    label.includes("confort") ||
    label.includes("premium") ||
    label.includes("comfort")
  ) {
    // imageConfortPremium (et non imageComfort, déjà utilisée par FieldPreview).
    return imageConfortPremium;
  }
  if (
    label.includes("ponctualité") ||
    label.includes("ponctualite") ||
    label.includes("punctual")
  ) {
    // Image ponctualité dédiée (plus juste que l'image passager générique, et
    // distincte du pool Services → pas de doublon inter-section).
    return imagePonctualite;
  }
  // Fallback : rotation d'un petit pool générique plutôt que TOUJOURS la même
  // image (sinon tous les bénéfices non matchés d'une page ville affichent le
  // même visuel → répétition). Choix déterministe par le titre du bénéfice.
  // Pool DISJOINT de FieldPreviewSection (Prise-en-charge/Ambiance) et du pool
  // Services → une même image ne se retrouve pas dans 2 sections de la page.
  const FALLBACK_POOL = [
    imagePonctualite,
    imageConfortPremium,
    imageSuiviVol,
    imageTarifsForfaitaires,
  ];
  // Rotation par INDEX (pas par hash) → 2 bénéfices non matchés ne tombent
  // jamais sur la même image du pool dans la même page.
  return FALLBACK_POOL[index % FALLBACK_POOL.length];
};

// Pool de secours (images distinctes) pour le dédoublonnage intra-section :
// si 2 bénéfices résolvent la MÊME image, le 2e reçoit la prochaine image
// encore inutilisée → aucune répétition dans la grille des bénéfices.
const BENEFIT_IMAGE_BACKUP: StaticImageData[] = [
  imagePonctualite,
  imageConfortPremium,
  imageSuiviVol,
  imageTarifsForfaitaires,
  imageChefDeProjet,
  imageParcAdaptable,
  imageItinerairesOptimises,
  imageSupportDedie,
  imageAccueilPancarte,
  imageSlaFiabilite,
];

/** Garantit des images distinctes dans une section (remplace les doublons). */
const dedupeSectionImages = (
  resolved: StaticImageData[],
  backup: StaticImageData[],
): StaticImageData[] => {
  const used = new Set<string>();
  return resolved.map((img) => {
    if (!used.has(img.src)) {
      used.add(img.src);
      return img;
    }
    const alt = backup.find((b) => !used.has(b.src)) ?? img;
    used.add(alt.src);
    return alt;
  });
};

export const BenefitsSection = ({
  heading,
  items,
  variant = "dark",
  locale = "fr",
}: BenefitsProps): JSX.Element => {
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);
  const isLight = variant === "light";
  const sectionBg = isLight ? "bg-white text-black" : "bg-black text-white";
  const cardBg = isLight ? "bg-white" : "bg-neutral-900/35";
  const borderCol = isLight ? "border-black/10" : "border-white/10";
  const subText = isLight ? "text-black/70" : "text-white/75";
  const benefitImages = dedupeSectionImages(
    items.map((b, i) => resolveBenefitImage(b.title, heading, i)),
    BENEFIT_IMAGE_BACKUP,
  );
  return (
    <section className={`relative py-16 ${sectionBg}`}>
      <div className="relative mx-auto max-w-[90rem] px-6 md:px-8">
        <p
          className={`mb-2 text-xs uppercase tracking-[0.18em] ${isLight ? "text-black/55" : "text-white/55"}`}
        >
          {ui.benefitsEyebrow}
        </p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          viewport={{ once: true, margin: "-10%" }}
          className="mb-3 font-space-age text-xl tracking-[0.2em]"
        >
          {heading}
        </motion.h2>
        <p className={`mb-6 max-w-3xl text-sm ${subText}`}>
          {ui.benefitsIntro}
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((b, idx) => (
            <motion.article
              key={b.title}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              viewport={{ once: true, margin: "-10%" }}
              className={`group flex h-full flex-col rounded-2xl border ${borderCol} ${cardBg} p-5 transition-colors hover:border-primary/40`}
            >
              <div className="mb-3 overflow-hidden rounded-xl border border-black/10">
                <div className="relative aspect-[21/9] w-full">
                  <Image
                    src={benefitImages[idx]}
                    alt={b.title}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
              </div>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full border ${isLight ? "border-black/15 bg-white" : "border-white/15 bg-black/60"} text-primary`}
                >
                  <Icon
                    icon={b.icon || "mdi:check-decagram"}
                    className="h-4 w-4"
                  />
                </span>
                <h3 className="text-sm font-semibold">{b.title}</h3>
              </div>
              <p className={`text-sm ${subText}`}>{b.description}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
